// admin/src/pages/AdminLive.jsx
import React, { useState, useEffect, useContext } from 'react';
import { Plus, Trash2, ExternalLink, Star, Layout, List } from 'lucide-react';
import { AuthContext } from '../AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const font = { mono: '"JetBrains Mono", monospace', condensed: '"Barlow Condensed", sans-serif', body: '"Barlow", sans-serif' };

const TIER_CONFIG = {
  hero:      { label: 'Hero',      desc: 'Main embed — 1 max',       color: '#fff' },
  secondary: { label: 'Secondary', desc: 'Featured cards — up to 4', color: '#ccc' },
  list:      { label: 'List',      desc: 'Scroll row — unlimited',   color: '#888' },
};

const AdminLive = () => {
  const { authToken }  = useContext(AuthContext);
  const [streamers, setStreamers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [login,     setLogin]     = useState('');
  const [tier,      setTier]      = useState('list');
  const [adding,    setAdding]    = useState(false);
  const [toast,     setToast]     = useState('');

  const headers   = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };
  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const load = () => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/admin/featured-streamers`, { headers })
      .then(r => r.json()).then(d => setStreamers(d.streamers || []))
      .catch(() => showToast('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    const cleanLogin = login.trim().replace('@', '').toLowerCase();
    if (!cleanLogin) { showToast('Enter a Twitch username'); return; }
    setAdding(true);
    try {
      let display_name = cleanLogin;
      let avatar_url   = '';
      try {
        const res = await fetch(`${BACKEND_URL}/api/profile/${cleanLogin}`, { headers });
        if (res.ok) {
          const data   = await res.json();
          display_name = data.user?.display_name || cleanLogin;
          avatar_url   = data.user?.profile_image_url || '';
        }
      } catch {}

      const res  = await fetch(`${BACKEND_URL}/api/admin/featured-streamers`, {
        method: 'POST', headers,
        body: JSON.stringify({ login: cleanLogin, display_name, avatar_url, tier }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.detail || 'Failed'); return; }
      showToast(data.message ? `✓ ${data.message}` : `✓ @${cleanLogin} added as ${tier}!`);
      setLogin(''); load();
    } catch { showToast('Failed to add'); }
    finally { setAdding(false); }
  };

  const updateTier = async (id, newTier) => {
    const res = await fetch(`${BACKEND_URL}/api/admin/featured-streamers/${id}/tier`, {
      method: 'PATCH', headers, body: JSON.stringify({ tier: newTier }),
    });
    if (res.ok) { showToast(`✓ Moved to ${newTier}`); load(); }
    else showToast('Failed');
  };

  const remove = async (id, login) => {
    if (!window.confirm(`Remove @${login}?`)) return;
    const res = await fetch(`${BACKEND_URL}/api/admin/featured-streamers/${id}`, { method: 'DELETE', headers });
    if (res.ok) { showToast('Removed'); load(); }
    else showToast('Failed');
  };

  const grouped = {
    hero:      streamers.filter(s => s.tier === 'hero'),
    secondary: streamers.filter(s => s.tier === 'secondary'),
    list:      streamers.filter(s => s.tier === 'list'),
  };

  const S = {
    page:     { padding: '2.5rem', maxWidth: 860 },
    input:    { background: '#060606', border: '1px solid #1a1a1a', color: '#fff', padding: '0.58rem 0.85rem', fontFamily: font.mono, fontSize: '0.7rem', outline: 'none', flex: 1 },
    select:   { background: '#060606', border: '1px solid #1a1a1a', color: '#fff', padding: '0.58rem 0.85rem', fontFamily: font.mono, fontSize: '0.7rem', outline: 'none', cursor: 'pointer' },
    btn:      { background: '#fff', color: '#000', border: 'none', padding: '0.58rem 1.25rem', fontFamily: font.mono, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' },
    btnGhost: { background: 'none', border: '1px solid #1a1a1a', color: '#666', padding: '0.28rem 0.65rem', fontFamily: font.mono, fontSize: '0.58rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' },
  };

  return (
    <div style={S.page}>
      <style>{`@keyframes fadein{from{opacity:0}to{opacity:1}} select option{background:#060606;}`}</style>
      {toast && <div style={{ position:'fixed', bottom:'1.5rem', right:'1.5rem', background:'#111', border:'1px solid #222', color:'#fff', fontFamily:font.mono, fontSize:'0.7rem', padding:'0.75rem 1.25rem', zIndex:999, animation:'fadein 0.2s ease' }}>{toast}</div>}

      <div style={{ fontFamily:font.mono, fontSize:'0.58rem', color:'#333', letterSpacing:'0.2em', marginBottom:6 }}>// MANAGE</div>
      <div style={{ fontFamily:font.condensed, fontWeight:900, fontSize:'2.5rem', letterSpacing:'-0.02em', marginBottom:'0.5rem' }}>BGN Live</div>
      <p style={{ fontFamily:font.body, fontWeight:300, fontSize:'0.82rem', color:'#444', marginBottom:'2rem' }}>
        Add any Twitch streamer by username. Set their tier to control where they appear on the homepage.
      </p>

      {/* Tier legend */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0.75rem', marginBottom:'2rem' }}>
        {Object.entries(TIER_CONFIG).map(([key, cfg]) => (
          <div key={key} style={{ background:'#0a0a0a', border:'1px solid #111', padding:'0.85rem 1rem' }}>
            <div style={{ fontFamily:font.mono, fontSize:'0.62rem', color:cfg.color, letterSpacing:'0.1em', marginBottom:3 }}>{cfg.label.toUpperCase()}</div>
            <div style={{ fontFamily:font.body, fontSize:'0.75rem', color:'#444' }}>{cfg.desc}</div>
          </div>
        ))}
      </div>

      {/* Add form */}
      <div style={{ fontFamily:font.mono, fontSize:'0.58rem', color:'#333', letterSpacing:'0.15em', marginBottom:'0.75rem' }}>// ADD STREAMER</div>
      <div style={{ display:'flex', gap:'0.75rem', marginBottom:'2.5rem', flexWrap:'wrap' }}>
        <input style={S.input} placeholder="Twitch username..." value={login} onChange={e=>setLogin(e.target.value)} onKeyDown={e=>e.key==='Enter'&&add()}/>
        <select style={S.select} value={tier} onChange={e=>setTier(e.target.value)}>
          <option value="hero">Hero (main embed)</option>
          <option value="secondary">Secondary (feature card)</option>
          <option value="list">List (scroll row)</option>
        </select>
        <button style={S.btn} onClick={add} disabled={adding}><Plus size={13}/>{adding?'ADDING...':'ADD'}</button>
      </div>

      {/* Grouped by tier */}
      {loading ? (
        <p style={{ fontFamily:font.mono, fontSize:'0.68rem', color:'#333' }}>LOADING...</p>
      ) : streamers.length === 0 ? (
        <div style={{ background:'#0a0a0a', border:'1px solid #111', padding:'3rem', textAlign:'center' }}>
          <p style={{ fontFamily:font.mono, fontSize:'0.62rem', color:'#333', letterSpacing:'0.1em' }}>NO STREAMERS ADDED YET</p>
        </div>
      ) : (
        Object.entries(grouped).map(([tierKey, items]) => items.length > 0 && (
          <div key={tierKey} style={{ marginBottom:'1.5rem', background:'#0a0a0a', border:'1px solid #111' }}>
            <div style={{ padding:'0.75rem 1.1rem', borderBottom:'1px solid #111', display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontFamily:font.mono, fontSize:'0.6rem', color:TIER_CONFIG[tierKey].color, letterSpacing:'0.15em', textTransform:'uppercase' }}>{TIER_CONFIG[tierKey].label}</span>
              <span style={{ fontFamily:font.body, fontSize:'0.72rem', color:'#333' }}>{TIER_CONFIG[tierKey].desc}</span>
              <span style={{ fontFamily:font.mono, fontSize:'0.58rem', color:'#444', marginLeft:'auto' }}>{items.length} streamers</span>
            </div>
            {items.map(s => (
              <div key={s.id} style={{ display:'flex', alignItems:'center', gap:'1rem', padding:'0.7rem 1.1rem', borderBottom:'1px solid #0d0d0d' }}
                onMouseEnter={e=>e.currentTarget.style.background='#111'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}
              >
                {s.avatar_url && <img src={s.avatar_url} alt="" style={{ width:26, height:26, borderRadius:'50%', filter:'grayscale(100%)', flexShrink:0 }}/>}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontFamily:font.mono, fontSize:'0.7rem', color:'#ccc' }}>@{s.login}</div>
                  {s.display_name && s.display_name !== s.login && <div style={{ fontFamily:font.body, fontSize:'0.7rem', color:'#444' }}>{s.display_name}</div>}
                </div>
                <div style={{ display:'flex', gap:4, flexShrink:0, flexWrap:'wrap' }}>
                  {tierKey !== 'hero'      && <button style={{ ...S.btnGhost, color:'#fff', borderColor:'#333' }} onClick={()=>updateTier(s.id,'hero')}>→ Hero</button>}
                  {tierKey !== 'secondary' && <button style={S.btnGhost} onClick={()=>updateTier(s.id,'secondary')}>→ Secondary</button>}
                  {tierKey !== 'list'      && <button style={S.btnGhost} onClick={()=>updateTier(s.id,'list')}>→ List</button>}
                  <button style={S.btnGhost} onClick={()=>window.open(`https://twitch.tv/${s.login}`,'_blank')}><ExternalLink size={10}/></button>
                  <button style={{ ...S.btnGhost, color:'#ff6b6b', borderColor:'#2a1010' }} onClick={()=>remove(s.id,s.login)}><Trash2 size={10}/></button>
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
};

export default AdminLive;